import { useEffect, useState } from "react";
import { AlertTriangle, GitMerge } from "lucide-react";
import { Modal } from "../lib/ui";
import { api, money } from "../lib/api";

const idOf = (c) => c?.tax_no || c?.tc_no || "—";

function CardChoice({ c, usage, isMain, onPick, testid }) {
  return (
    <label className={`merge-card ${isMain ? "main" : ""}`} data-testid={testid}>
      <input type="radio" checked={isMain} onChange={onPick} data-testid={`${testid}-radio`} />
      <div>
        <strong>{c.name}</strong>
        <small>{c.type === "corporate" ? "Kurumsal" : "Şahıs"} · TC/VKN: {idOf(c)}{c.phone ? ` · ${c.phone}` : ""}</small>
        <small>{[c.city, c.district].filter(Boolean).join(" / ") || "Adres yok"}</small>
        <small>Bakiye: <b>{money(c.balance || 0)}</b> · {usage === undefined ? "hareketler yükleniyor…" : usage || "hareket yok"}</small>
        <span className={`merge-badge ${isMain ? "keep" : "drop"}`}>{isMain ? "ANA KART · KALACAK" : "SİLİNECEK · hareketleri taşınacak"}</span>
      </div>
    </label>
  );
}

export function MergeModal({ a, b, reason, onClose, onMerged }) {
  const [mainId, setMainId] = useState(a.id);
  const [usage, setUsage] = useState({});
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    [a, b].forEach((c) => api.get(`/customers/${c.id}/usage`).then((r) => setUsage((u) => ({ ...u, [c.id]: r.data.summary }))).catch(() => {}));
  }, [a, b]);
  const main = mainId === a.id ? a : b;
  const other = mainId === a.id ? b : a;
  const idsDiffer = idOf(a) !== "—" && idOf(b) !== "—" && idOf(a) !== idOf(b);
  const merge = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/customers/merge", { main_id: main.id, other_id: other.id });
      onMerged(`${other.name} → ${main.name} birleştirildi${data.summary ? ` (${data.summary} taşındı)` : ""}.`);
    } catch (e) { setError(e.response?.data?.detail || "Birleştirilemedi"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Cari birleştir" eyebrow="CARİ BİRLEŞTİRME" size="lg" onClose={onClose} testid="merge-modal" subtitle="Kalacak ana kartı seçin">
      <div className={`merge-warn ${reason ? "" : "danger"}`} data-testid="merge-warning">
        <AlertTriangle size={16} />
        <span>{reason ? <>Bu iki kart <b>aynı firma</b> olarak tespit edildi ({reason}).</> : <>Bu iki kart sistem tarafından aynı firma olarak tespit <b>edilmedi</b>. Doğru carileri seçtiğinizden emin olun.</>}
          {idsDiffer && <> <b>TC/vergi numaraları farklı!</b></>}</span>
      </div>
      <div className="merge-grid">
        <CardChoice c={a} usage={usage[a.id]} isMain={mainId === a.id} onPick={() => setMainId(a.id)} testid="merge-card-a" />
        <CardChoice c={b} usage={usage[b.id]} isMain={mainId === b.id} onPick={() => setMainId(b.id)} testid="merge-card-b" />
      </div>
      <p className="muted merge-summary" data-testid="merge-summary">
        <b>{other.name}</b> kartındaki tüm faturalar, tahsilat/ödemeler, çekler, iş fişleri ve diğer kayıtlar <b>{main.name}</b> kartına taşınacak;
        ana karttaki boş alanlar (telefon, adres, vergi no…) diğer karttan doldurulacak, açılış bakiyeleri toplanacak ve <b>{other.name}</b> kartı silinecek.
      </p>
      <label className="merge-confirm"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="merge-confirm-check" /> Okudum, bu iki cariyi birleştir</label>
      {error && <div className="form-error" data-testid="merge-error">{error}</div>}
      <div className="modal-actions">
        <button type="button" className="secondary-button" onClick={onClose} data-testid="merge-cancel">Vazgeç</button>
        <button type="button" className="primary-button" disabled={!ok || busy} onClick={merge} data-testid="merge-submit">
          <GitMerge size={15} /> {busy ? "Birleştiriliyor…" : "Birleştir"}
        </button>
      </div>
    </Modal>
  );
}

export function DuplicatesModal({ pairs, byId, onClose, onPick }) {
  const list = pairs.filter((p) => byId[p.a] && byId[p.b]);
  return (
    <Modal title="Olası tekrar eden cariler" eyebrow="CARİ BİRLEŞTİRME" size="lg" onClose={onClose} testid="duplicates-modal"
           subtitle="Aynı TC/vergi no veya çok benzer isimli kartlar">
      {!list.length && <p className="muted" data-testid="duplicates-empty">Tekrar eden cari bulunamadı.</p>}
      <div className="dup-list">
        {list.map((p) => (
          <div key={`${p.a}-${p.b}`} className="dup-row" data-testid={`dup-row-${p.a}-${p.b}`}>
            <div><strong>{byId[p.a].name}</strong><small>{idOf(byId[p.a])} · {money(byId[p.a].balance || 0)}</small></div>
            <div><strong>{byId[p.b].name}</strong><small>{idOf(byId[p.b])} · {money(byId[p.b].balance || 0)}</small></div>
            <span className="dup-reason">{p.reason}</span>
            <button type="button" className="secondary-button" data-testid={`dup-merge-${p.a}-${p.b}`} onClick={() => onPick(p)}>
              <GitMerge size={14} /> Birleştir
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
